import { useEffect } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import to from 'await-to-js'

import { Modal } from 'components/UI/Modal/Modal'
import { TextInput } from 'components/FormUtils/TextInput'
import FbButton from 'components/FbUI/FbButton'
import { handleFormError } from 'utils/handleFormError'

import { Sequence, useUpdateSequence } from '../sequences_api'
import {
  SequenceForm,
  SequenceStepFormItem,
  defaultStep,
} from './SequenceStepFormItem'

interface EditSequenceModalProps {
  sequence: Sequence | null
  campaignId: number
  isOpen: boolean
  onClose: () => void
}

export function EditSequenceModal({
  sequence,
  campaignId,
  isOpen,
  onClose,
}: EditSequenceModalProps) {
  const updateSequenceMutation = useUpdateSequence(campaignId)

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    reset,
    watch,
  } = useForm<SequenceForm>({
    defaultValues: {
      title: '',
      steps: [],
    },
    mode: 'onChange',
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'steps',
  })

  const watchedSteps = watch('steps')

  useEffect(() => {
    if (sequence && isOpen) {
      reset({
        title: sequence.title,
        steps: sequence.steps
          .sort((a, b) => a.order - b.order)
          .map((step) => ({
            id: step.id, // Preserve bison step ID for updates
            email_subject: step.email_subject,
            email_body: step.email_body,
            wait_in_days: step.wait_in_days,
          })),
      })
    }
  }, [sequence, isOpen, reset])

  const onSubmit = handleSubmit(async (values) => {
    if (!sequence) return

    const payload = {
      title: values.title,
      steps: values.steps.map((step, index) => ({
        id: step.id, // Required for updates - bison step ID
        order: index + 1,
        email_subject: step.email_subject,
        email_body: step.email_body,
        wait_in_days: Math.max(1, step.wait_in_days),
      })),
    }

    const [err] = await to(
      updateSequenceMutation.mutateAsync({
        sequenceId: sequence.id,
        payload,
      })
    )

    if (err) {
      handleFormError(err, setError)
      return
    }

    toast.success('Sequence updated successfully')
    onClose()
  })

  const addStep = () => {
    append({ ...defaultStep })
  }

  const canSubmit = isValid && watchedSteps.length > 0

  return (
    <Modal
      open={isOpen}
      title="Edit Email Sequence"
      description="Update your email sequence settings."
      onOpenChange={(open) => !open && onClose()}
      loading={isSubmitting}
      blockAccept={!canSubmit}
      onAccept={onSubmit}
      acceptButtonText="Save Changes"
      size="xl"
    >
      <div className="space-y-6">
        <TextInput
          name="title"
          control={control}
          label="Sequence Title"
          placeholder="Enter sequence title"
          rules={{ required: 'Title is required' }}
        />

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900">Email Steps</h3>
            <FbButton size="sm" variant="secondary" onClick={addStep}>
              Add Step
            </FbButton>
          </div>

          <div className="space-y-4">
            {fields.map((field, index) => (
              <SequenceStepFormItem
                key={field.id}
                field={field}
                index={index}
                control={control}
                canRemove={fields.length > 1}
                isLast={index === fields.length - 1}
                onRemove={() => remove(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}
